import React from "react";
import { HiTrophy } from "react-icons/hi2";
import { EventVM, eventDayIndexForKey } from "./eventVM";
import PerformanceIntentBadge from "../shared/PerformanceIntentBadge";
import EventTagChips from "../shared/EventTagChips";
import { formatDateRange } from "../../utils/goalDates";

interface EventCardProps {
  event: EventVM;
  /** YYYY-MM-DD of the row the card sits in (for "Day n of m"). */
  dateKey?: string;
  /** "list" shows the date range; "compact" omits it. */
  variant?: "list" | "compact";
  /** True when any training log references this event. */
  logged?: boolean;
  onClick?: () => void;
}

/**
 * Schedule card for an event (Events.md §4). Coloured by event type; shows a
 * type badge, a derived Logged pill and (list variant) up to two coach tags.
 */
const EventCard: React.FC<EventCardProps> = ({
  event,
  dateKey,
  variant = "list",
  logged = false,
  onClick,
}) => {
  const dayIndex = dateKey ? eventDayIndexForKey(event, dateKey) : null;
  const withdrawn = event.event.status === "withdrawn";
  return (
    <div
      onClick={onClick}
      role={onClick ? "button" : undefined}
      className={`group relative flex items-start gap-3 rounded-xl border bg-base-100 border-base-300 hover:shadow-md p-4 transition-all ${
        withdrawn ? "opacity-80" : ""
      } ${onClick ? "cursor-pointer" : ""}`}
    >
      <span
        className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-white"
        style={{ backgroundColor: event.color }}
        aria-hidden
      >
        <HiTrophy className="w-4 h-4" />
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <h3 className="text-base font-semibold text-base-content font-grotesk truncate">
            {event.name}
          </h3>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {withdrawn ? (
              <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide font-grotesk bg-base-200 text-base-content/60">
                Withdrawn
              </span>
            ) : (
              logged && (
                <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide font-grotesk bg-success/15 text-success">
                  Logged
                </span>
              )
            )}
          </div>
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-sm font-grotesk text-base-content/60">
          <PerformanceIntentBadge type={event.type} />
          {dayIndex && (
            <>
              <span className="text-base-content/30">·</span>
              <span className="font-geist tabular-nums">
                Day {dayIndex} of {event.dayKeys.length}
              </span>
            </>
          )}
        </div>
        {variant === "list" && (
          <p className="mt-1 text-sm text-base-content/50 font-grotesk">
            {formatDateRange(event.date, event.endDate)}
          </p>
        )}
        {/* Week / month / year views don't use this card, so tags only
            appear in the detailed list + day views. */}
        <EventTagChips tags={event.event.tags} max={2} className="mt-1.5" />
      </div>
    </div>
  );
};

export default EventCard;
